import { Col, Modal, Row, Spin, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { DraggableTitle, DraggableWrapper } from "@ui/modals/draggable-wrapper";
import Loader from "@ui/feedback/loader";
import PersonEmailTemplateForm from "./person-email-template-form";
import PersonRecipientsList from "./person-recipients-list";

import { getMessageTemplates, getTemplateFields } from "@redux/actionCreators/bookingSetup";

import constants from "@constants/constants";

import "@app/features/system-setup/ui/message-templates/message-template-modal/styles.scss";
import "./send-email-template-modal.scss";

const { Title } = Typography;
const { NULLIFY } = constants;

/**
 * Displays a modal used for selecting and sending an email template
 * to a list of selected persons.
 *
 * @param {Object} props - component Props
 * @param {boolean} props.isVisible - Controls modal visibility.
 * @param {Array<{ObjectId: string, Name: string}>} [props.selectedPersons=[]] - List of selected persons.
 * @param {Function} [props.resetModalFn] - Function to reset/close the modal.
 * @returns {JSX.Element}
 */
const SendEmailByTemplateToPersonModal = ({
  isVisible,
  selectedPersons = [],
  resetModalFn = () => {}
}) => {
  const dispatch = useDispatch();
  const [persons, setPersons] = useState(selectedPersons);

  const bookingSetupReducer = useSelector(state => state.bookingSetupReducer || null, shallowEqual);

  const {
    addMessageTemplate: {
      isLoading: addMessageTemplateLoading,
      resultData: addMessageTemplateData
    },
    sendMessageTemplateEmail: { isLoading: sendMessageTemplateEmailLoading },
    getMessageTemplates: { isLoading: getMessageTemplatesLoading },
    getTemplateFields: { isLoading: getTemplateFieldsLoading }
  } = bookingSetupReducer;

  const handleRemovePerson = personId => {
    const filteredPersons = persons.filter(({ ObjectId }) => ObjectId !== personId);
    setPersons(filteredPersons);
  };

  // get initial data - update data after saving a template
  useEffect(() => {
    dispatch(getMessageTemplates());
    dispatch(getTemplateFields());
  }, [addMessageTemplateData]);

  return (
    <Modal
      title={<DraggableTitle title="Send group email" />}
      modalRender={modal => <DraggableWrapper>{modal}</DraggableWrapper>}
      open={isVisible}
      footer={NULLIFY}
      zIndex="1810"
      onCancel={() => resetModalFn()}
      maskClosable={false}
      width={"75%"}
      centered
      className="drs-modal-css send-template-email-candidate"
    >
      <Spin
        indicator={<Loader />}
        spinning={
          getMessageTemplatesLoading ||
          getTemplateFieldsLoading ||
          sendMessageTemplateEmailLoading ||
          addMessageTemplateLoading
        }
        className="loaderGifPosition"
      >
        <Row gutter={24} className="mb-4">
          <Col span={7}>
            <Title className="p-4" level={4}>
              Recipients ({persons.length})
            </Title>
            <PersonRecipientsList persons={persons} handleRemovePerson={handleRemovePerson} />
          </Col>

          <Col span={17}>
            <PersonEmailTemplateForm
              persons={persons}
              resetModalFn={resetModalFn}
              selectedPersons={selectedPersons}
            />
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};

export default React.memo(SendEmailByTemplateToPersonModal);